import { Fragment, ReactNode, useState } from 'react';

import { TableHeaderRow } from 'routes/singleEvent/components/EventArenaSection/TableHeaderRow';

import CircleSpinner from 'components/circleSpinner/CircleSpinner';
import { Pagination } from 'components/pagination';

import { useDailyRewardHistoryQuery } from '../hooks/useDailyRewardHistoryQuery';

import { DailyRewardsModal } from '../DailyRewardsModal';

const COLUMNS = [
  { key: 'date', header: 'Date' },
  { key: 'calendarDay', header: 'Calendar Day' },
  { key: 'reward', header: 'Reward' },
];

const ColumnSeparator = () => <div className="tw-h-[8px] tw-w-[0.5px] tw-shrink-0 tw-bg-white lg:tw-hidden" />;

const renderCellsWithSeparators = (cells: ReactNode[]) =>
  cells.map((cell, i) => (
    <Fragment key={i}>
      {i > 0 && <ColumnSeparator />}
      {cell}
    </Fragment>
  ));

const TableRow = ({ children }: { children: ReactNode }) => (
  <div className="tw-flex tw-h-[45px] tw-flex-row tw-items-center tw-gap-[8px] tw-bg-rewardHistoryRowGradient tw-px-[16px] lg:tw-h-[44px] lg:tw-gap-[16px]">
    {children}
  </div>
);

const cellClassName =
  'tw-flex-1 tw-text-[10px] lg:tw-leading-[24px] tw-text-white lg:tw-text-[15px] tw-line-clamp-2 lg:tw-line-clamp-1';

type RewardHistoryModalProps = {
  onClose: () => void;
  onPrevious?: () => void;
};

export const RewardHistoryModal = ({ onClose, onPrevious }: RewardHistoryModalProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { displayItems, isEmpty, showPagination, totalPages, hasNextPage, hasPreviousPage, isLoading, error } =
    useDailyRewardHistoryQuery(currentPage);

  const goToPage = (page: number) => setCurrentPage(page);
  const goToNextPage = () => hasNextPage && setCurrentPage((p) => p + 1);
  const goToPreviousPage = () => hasPreviousPage && setCurrentPage((p) => p - 1);

  return (
    <DailyRewardsModal
      onClose={onClose}
      onPrevious={onPrevious}
      title="Calendar Rewards History"
      className="lg:tw-max-w-[1200px]"
    >
      <div className="tw-px-[30px] tw-pb-[30px] tw-font-jost">
        <div className="tw-mt-[30px]">
          {isLoading ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-[60px]">
              <CircleSpinner size={48} />
            </div>
          ) : error ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-[60px] tw-text-[14px] tw-text-white/60">
              Failed to load reward history. Please try again.
            </div>
          ) : (
            <>
              <div className="tw-mb-[8px] tw-flex tw-flex-row tw-items-center tw-gap-[16px] tw-px-[16px] tw-text-white lg:tw-mb-[30px]">
                {COLUMNS.map((column) => (
                  <TableHeaderRow key={column.key} title={column.header} variant="rewardDistributionHistory" />
                ))}
              </div>

              <div className="tw-flex tw-flex-col tw-gap-[6px] lg:tw-gap-[20px]">
                {isEmpty ? (
                  <TableRow>
                    {renderCellsWithSeparators(
                      COLUMNS.map((column) => (
                        <div key={column.key} className={cellClassName}>
                          -
                        </div>
                      ))
                    )}
                  </TableRow>
                ) : (
                  displayItems.map((item) => (
                    <TableRow key={item.id}>
                      {renderCellsWithSeparators([
                        <div className={cellClassName}>{item.date}</div>,
                        <div className={cellClassName}>{item.calendarDay}</div>,
                        <div className={cellClassName}>{item.reward}</div>,
                      ])}
                    </TableRow>
                  ))
                )}
              </div>

              {showPagination && totalPages > 1 && (
                <>
                  <div className="tw-mb-[20px] lg:tw-mb-[38px]" />
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    hasNextPage={hasNextPage}
                    hasPreviousPage={hasPreviousPage}
                    goToPage={goToPage}
                    goToNextPage={goToNextPage}
                    goToPreviousPage={goToPreviousPage}
                    variant="taskBased"
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </DailyRewardsModal>
  );
};
